// blit.js — present a native-resolution decoded frame at device-pixel size.
//
// The renderers (lcd.js, crt.js) decode guest VRAM 1:1 into an ImageData at
// the mode's native geometry. Presenting that directly on a CSS-stretched
// canvas leaves the scaling to the compositor: `image-rendering: pixelated`
// gives uneven nearest-neighbour blocks at fractional zooms, `auto` gives
// bilinear-from-1x mush. Instead this presenter:
//
//   1. keeps the native frame on an offscreen canvas,
//   2. sizes the visible canvas backing store to the element's CSS box times
//      devicePixelRatio (so a 640x80 CSS panel on a 2x display is backed by
//      1280x160 device pixels — the "retina" path),
//   3. drawImage()s the native frame across the whole backing store with
//      imageSmoothingEnabled chosen per scale: OFF for integer multiples
//      (pixel-perfect doubling), HIGH-quality smoothing for fractional zooms
//      (antialiased instead of blocky).
//
// The visible canvas must NOT carry `image-rendering: pixelated` — its backing
// store already matches device pixels, so no further CSS resampling happens.
export class ScaledPresenter {
  constructor(canvas, { smoothFractional = true, alpha = false } = {}) {
    this.canvas = canvas;
    this.smoothFractional = smoothFractional;
    this.alpha = alpha;
    this.ctx = canvas.getContext('2d', { alpha });
    this.off = document.createElement('canvas');
    this.offCtx = this.off.getContext('2d', { alpha });
  }

  // Blit `image` (ImageData at native guest geometry) to the visible canvas.
  present(image) {
    if (this.off.width !== image.width || this.off.height !== image.height) {
      this.off.width = image.width;
      this.off.height = image.height;
    }
    this.offCtx.putImageData(image, 0, 0);
    this._sizeBacking();
    const bw = this.canvas.width, bh = this.canvas.height;
    const sx = bw / image.width, sy = bh / image.height;
    const integer = Math.abs(sx - Math.round(sx)) < 1e-3 &&
                    Math.abs(sy - Math.round(sy)) < 1e-3;
    this.ctx.imageSmoothingEnabled = this.smoothFractional && !integer;
    this.ctx.imageSmoothingQuality = 'high';
    // Transparent presenters carry only the active layer. Erase the previous
    // frame before compositing so cells which turned off become transparent.
    if (this.alpha) this.ctx.clearRect(0, 0, bw, bh);
    this.ctx.drawImage(this.off, 0, 0, bw, bh);
  }

  // Fill the whole panel with a flat colour (power-off / blank states).
  fill(cssColor) {
    this._sizeBacking();
    this.ctx.fillStyle = cssColor;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  // Clear an alpha-backed presentation layer (for reflective displays whose
  // unenergised glass is supplied by the element below the canvas).
  clear() {
    this._sizeBacking();
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  _sizeBacking() {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    // Element not laid out yet (display:none ancestors): keep whatever backing
    // store exists rather than collapsing to 1x1 and losing the frame.
    if (!rect.width || !rect.height) return;
    const bw = Math.max(1, Math.round(rect.width * dpr));
    const bh = Math.max(1, Math.round(rect.height * dpr));
    if (this.canvas.width !== bw || this.canvas.height !== bh) {
      this.canvas.width = bw;
      this.canvas.height = bh;
    }
  }
}
